import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineTrash, HiPlus, HiMinus, HiOutlineArrowLeft, HiOutlineShoppingBag } from "react-icons/hi";
import Footer from '../../Components/Footer/Footer';
import EmptyCart from '../../Images/CartImages/EmptyCart.png';
import Loading1 from "../../Components/Loading/Loading1";
import CustomNav from "../../Components/Navbar/NewNavbar";

const Cart = () => {
    const [cart, setCart] = useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const storedCart = localStorage.getItem("cart");
        if (storedCart) {
            setCart(JSON.parse(storedCart));
        }
        
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 800);
        return () => clearTimeout(timer);
    }, []);

    const removeFromCart = (index) => {
        let cartItems = [...cart];
        cartItems.splice(index, 1);
        updateCartState(cartItems);
    };

    const updateQty = (index, delta) => {
        let cartItems = [...cart];
        cartItems[index].qty += delta;
        if (cartItems[index].qty <= 0) {
            cartItems.splice(index, 1);
        }
        updateCartState(cartItems);
    };

    const updateCartState = (cartItems) => {
        setCart(cartItems);
        localStorage.setItem("cart", JSON.stringify(cartItems));
    };

    const calculateSubtotal = () => {
        return cart.reduce((total, item) => total + (item.discountedPrice * (item.qty || 1)), 0);
    };

    const handleCheckout = () => {
        navigate('/checkout', { state: { cart } });
    };

    if (isLoading) return <Loading1 />;

    return (
        <div className="bg-white min-h-screen">
            <CustomNav />
            
            <section className="pt-32 pb-20">
                <div className="container-custom">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                        <div className="space-y-2">
                            <h1 className="text-4xl md:text-5xl font-heading font-black text-foreground">Your <span className="text-primary italic">Selection</span></h1>
                            <p className="text-muted-foreground font-medium">Review your premium cleaning services</p>
                        </div>
                        <Link to="/" className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest text-primary hover:gap-3 transition-all">
                            <HiOutlineArrowLeft size={18} />
                            Continue Exploring
                        </Link>
                    </div>

                    {cart.length === 0 ? (
                        <div className="card-premium py-24 text-center space-y-8 bg-secondary/30 border-dashed border-2">
                            <div className="relative w-48 h-48 mx-auto">
                                <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-20"></div>
                                <div className="relative z-10 w-full h-full bg-white rounded-full flex items-center justify-center shadow-xl">
                                    <HiOutlineShoppingBag size={80} className="text-primary/20" />
                                </div>
                            </div>
                            <div className="space-y-3 max-w-sm mx-auto">
                                <h2 className="text-2xl font-bold">Your cart is empty</h2>
                                <p className="text-muted-foreground leading-relaxed">It looks like you haven't selected any premium services yet. Let's find the perfect cleaning package for you.</p>
                            </div>
                            <Link to="/" className="btn-primary inline-block !px-10">Discover Services</Link>
                        </div>
                    ) : (
                        <div className="grid lg:grid-cols-12 gap-12">
                            {/* Items List */}
                            <div className="lg:col-span-8 space-y-6">
                                {cart.map((item, index) => (
                                    <div key={index} className="card-premium p-6 flex flex-col sm:flex-row gap-6 group hover:border-primary/20 transition-all">
                                        <div className="w-full sm:w-40 h-40 shrink-0 rounded-2xl overflow-hidden relative">
                                            <img src={item.img} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                        </div>
                                        
                                        <div className="flex-grow flex flex-col justify-between py-2">
                                            <div className="space-y-2">
                                                <div className="flex items-start justify-between">
                                                    <h3 className="text-xl font-bold text-foreground">{item.name}</h3>
                                                    <button 
                                                        onClick={() => removeFromCart(index)}
                                                        className="p-2 text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all rounded-lg"
                                                    >
                                                        <HiOutlineTrash size={22} />
                                                    </button>
                                                </div>
                                                <p className="text-sm text-muted-foreground line-clamp-2 max-w-md">
                                                    {Array.isArray(item.description) ? item.description[0] : item.description}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between mt-6">
                                                <div className="flex items-center bg-secondary rounded-xl p-1 gap-4">
                                                    <button 
                                                        onClick={() => updateQty(index, -1)}
                                                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-white text-primary hover:bg-primary hover:text-white transition-all shadow-sm"
                                                    >
                                                        <HiMinus size={16} />
                                                    </button>
                                                    <span className="font-black text-lg w-6 text-center">{item.qty || 1}</span>
                                                    <button 
                                                        onClick={() => updateQty(index, 1)}
                                                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-white text-primary hover:bg-primary hover:text-white transition-all shadow-sm"
                                                    >
                                                        <HiPlus size={16} />
                                                    </button>
                                                </div>
                                                <div className="text-right">
                                                    <span className="text-2xl font-black text-primary">₹{item.discountedPrice * (item.qty || 1)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Summary */}
                            <div className="lg:col-span-4 lg:sticky lg:top-32 h-fit">
                                <div className="card-premium p-8 bg-secondary/50 border-primary/10">
                                    <h2 className="text-2xl font-bold mb-8">Order Summary</h2>
                                    
                                    <div className="space-y-4 pb-8 border-b border-border">
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Subtotal ({cart.length} items)</span>
                                            <span className="font-bold text-foreground">₹{calculateSubtotal()}</span>
                                        </div>
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Service Fee</span>
                                            <span className="font-bold text-green-600">FREE</span>
                                        </div>
                                        <div className="flex justify-between text-muted-foreground">
                                            <span>Tax (GST)</span>
                                            <span className="font-bold text-foreground">Calculated at checkout</span>
                                        </div>
                                    </div>

                                    <div className="pt-8 space-y-8">
                                        <div className="flex justify-between items-end">
                                            <span className="text-lg font-bold">Total Amount</span>
                                            <span className="text-4xl font-black text-primary">₹{calculateSubtotal()}</span>
                                        </div>
                                        
                                        <div className="space-y-4">
                                            <button 
                                                onClick={handleCheckout} 
                                                className="btn-primary w-full !py-5 shadow-xl hover:shadow-2xl shadow-primary/20 text-lg"
                                            >
                                                Secure Checkout
                                            </button>
                                            <p className="text-[10px] text-center text-muted-foreground font-medium uppercase tracking-widest">
                                                Guaranteed safe checkout with 256-bit encryption
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mt-8 p-6 rounded-3xl bg-primary/5 flex items-start gap-4">
                                    <div className="w-10 h-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                        <HiPlus size={20} className="rotate-45" />
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm font-bold">Price Match Guarantee</p>
                                        <p className="text-xs text-muted-foreground leading-relaxed">Fould a better price? We'll match it and give you an extra 10% off.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Cart;

